import { FC, useState } from "react";

import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Button,
  ButtonText,
  DropdownPicker,
  Heading,
  HStack,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import { Question } from "@/types/resources";
import { QuestionType, RuleOperator } from "@/types/utils";
import { operatorsForQuestionType, useOperatorLabels } from "./operators";

interface RuleEditorProps {
  allQuestions: Question[];
  currentQuestionOrder?: number;
  /** Operator the rule currently uses (defaults the picker on open). */
  initialOperator: RuleOperator;
  isOpen: boolean;
  onClose: () => void;
  /** Persist an operator change on an already-saved rule (keeps dialog open). */
  onOperatorChange?: (operator: RuleOperator) => void;
  onSave: (targetQuestionId: string | null, operator: RuleOperator) => void;
  optionText: string;
  questionType: QuestionType;
  targetQuestionId: string | null;
}

const RuleEditor: FC<RuleEditorProps> = ({
  allQuestions,
  currentQuestionOrder,
  initialOperator,
  isOpen,
  onClose,
  onOperatorChange,
  onSave,
  optionText,
  questionType,
  targetQuestionId,
}) => {
  const { i18n } = useLocalization();
  const operatorLabels = useOperatorLabels();
  const [operator, setOperator] = useState<RuleOperator>(initialOperator);

  // Category assignment routes the assigned track (eq) and has no operator
  // choice; single/multiple choice let the author pick one.
  const canChooseOperator =
    questionType === "single_choice" || questionType === "multiple_choice";

  // Only show questions after the current one
  const availableQuestions = allQuestions
    .filter(
      (q) =>
        currentQuestionOrder !== undefined && q.order > currentQuestionOrder
    )
    .sort((a, b) => a.order - b.order);

  const dropdownItems = [
    { label: i18n.t("questions.endSurvey"), value: "__END_SURVEY__" },
    ...availableQuestions.map((q) => ({
      label: `${q.order}. ${q.text}`,
      value: q.id,
    })),
  ];

  const operatorItems = operatorsForQuestionType(questionType).map((op) => ({
    label: operatorLabels[op],
    value: op,
  }));

  // Map the targetQuestionId: null means no rule, "__END_SURVEY__" means end survey
  const hasRule = targetQuestionId !== null;

  const handleOperatorChange = (value: string) => {
    const next = value as RuleOperator;
    setOperator(next);
    // Live-update an existing rule; a not-yet-created rule picks it up on save.
    if (hasRule) onOperatorChange?.(next);
  };

  return (
    <AlertDialog isOpen={isOpen} onClose={onClose}>
      <AlertDialogBackdrop />
      <AlertDialogContent className="gap-6">
        <AlertDialogHeader>
          <Heading size="lg">{i18n.t("questions.skipLogic")}</Heading>
        </AlertDialogHeader>
        <AlertDialogBody>
          <VStack className="gap-4">
            <HStack className="items-center gap-2 flex-wrap">
              <Text className="text-typography-700">
                {i18n.t("questions.whenAnswer")}
              </Text>
              {canChooseOperator ? (
                <DropdownPicker
                  items={operatorItems}
                  onChange={handleOperatorChange}
                  placeholder={i18n.t("questions.selectOption")}
                  value={operator}
                />
              ) : (
                <Text className="text-typography-700">
                  {operatorLabels[operator]}
                </Text>
              )}
              <Text className="font-semibold text-typography-700">
                {`"${optionText}"`}
              </Text>
            </HStack>
            <Text className="text-typography-700">
              {i18n.t("questions.skipToLabel")}
            </Text>
            <DropdownPicker
              items={dropdownItems}
              onChange={(value) => onSave(value, operator)}
              placeholder={i18n.t("questions.selectOption")}
              value={targetQuestionId}
            />
          </VStack>
        </AlertDialogBody>
        <AlertDialogFooter>
          <HStack className="gap-3">
            <Button onPress={onClose} variant="outline">
              <ButtonText>{i18n.t("misc.close")}</ButtonText>
            </Button>
            {hasRule && (
              <Button
                className="bg-error-600"
                onPress={() => onSave(null, operator)}
              >
                <ButtonText className="text-white whitespace-nowrap">
                  {i18n.t("questions.removeRule")}
                </ButtonText>
              </Button>
            )}
          </HStack>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default RuleEditor;
